import React, {ReactElement} from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import {concatenate} from "services/string";
import Button from "../Button";

enum Type { INFO, SUCCESS, WARNING, ERROR }

type AlertProps = {
    type?: Type,
    message: string
    buttonOnClick?: () => void;
    buttonText?: string;
    buttonIcon?: (props: React.SVGProps<SVGSVGElement>) => ReactElement
}

const bannerClasses: Record<Type, string> = {
    [Type.INFO]: 'bg-blue-50 border-blue-400',
    [Type.SUCCESS]: 'bg-green-50 border-green-400',
    [Type.WARNING]: 'bg-yellow-50 border-yellow-400',
    [Type.ERROR]: 'bg-red-100 border-red-400',
}

const messageClasses: Record<Type, string> = {
    [Type.INFO]: 'text-blue-700',
    [Type.SUCCESS]: 'text-green-700',
    [Type.WARNING]: 'text-yellow-700',
    [Type.ERROR]: 'text-red-700',
}

const icons: Record<Type, ReactElement> = {
    [Type.INFO]: <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true"/>,
    [Type.SUCCESS]: <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true"/>,
    [Type.WARNING]: <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true"/>,
    [Type.ERROR]: <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true"/>,
}

export default function Alert({type = Type.INFO, message, buttonOnClick, buttonText, buttonIcon}: AlertProps) {
    return (
        <div className={concatenate('border-l-4 pl-4 py-3 pr-4 rounded-md', bannerClasses[type])}>
            <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                    {icons[type]}
                </div>
                <p className={concatenate('text-sm', messageClasses[type])}>
                    {message}
                </p>

                {buttonOnClick &&
                    <div className='ml-auto'>
                        <Button
                          color={Button.colors.PRIMARY}
                          size={Button.sizes.X_SMALL}
                          icon={buttonIcon}
                          onClick={buttonOnClick}
                        >
                            {buttonText}
                        </Button>
                    </div>
                }
            </div>
        </div>
    );
}

Alert.types = Type;